import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Users } from "lucide-react";
import { useLang } from "@/i18n";
import { PHOTOS } from "@/photos";
import { MaskedLine, EASE } from "./Reveal";
import BookingBar from "./BookingBar";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.24]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative flex min-h-[100svh] flex-col overflow-hidden bg-stone-950">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={PHOTOS.hero}
          alt="Welcome Love House Roma"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/40 to-stone-950/50" />
      <div className="animate-drift absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
      <div className="animate-drift-slow absolute -right-24 bottom-1/3 h-80 w-80 rounded-full bg-pink-500/20 blur-3xl" />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end gap-10 px-5 pb-12 pt-36 sm:px-8"
      >
        <div className="max-w-3xl">
          <MaskedLine delay={0.35}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-teal-200 backdrop-blur-md">
              <MapPin className="h-3.5 w-3.5 text-pink-400" />
              {t.hero.eyebrow}
            </span>
          </MaskedLine>

          <h1 data-testid="hero-title" className="font-display mt-6 text-5xl font-bold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
            <MaskedLine delay={0.5}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="text-gradient-brand italic">{t.hero.line2}</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.85}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-200 sm:text-lg">
              {t.hero.tagline}
            </p>
          </MaskedLine>

          <MaskedLine delay={1}>
            <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
              <Users className="h-4 w-4 text-teal-300" />
              {t.hero.badge}
            </span>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
        >
          <BookingBar />
        </motion.div>
      </motion.div>
    </section>
  );
}
